// Shared composer input — textarea-backed (no contenteditable, no DOM writes).
// Usage:
//   ComposerBox: {
//     placeholder: 'Ask a question…',
//     _onSend: (body, el, s) => { /* handle send */ }
//   }
//
// NOTE: the callback is named `_onSend` (underscore prefix), NOT `onSend`.
// DOMQL treats any `onXxx` property (where X is uppercase) as a DOM event
// handler and registers it as an event listener for the synthetic 'send'
// DOM event — which never fires. The underscore prefix keeps it as a plain
// property that _submit can read via el.parent._onSend.
//
// The textarea's value is driven by component state `_composerDraft` so
// clearing after submit is a state update — no `el.node.innerHTML = ''` or
// other DOM mutations. Each ComposerBox gets its own local state slot
// (DOMQL auto-creates `_composerDraft` on first update), so multiple
// composers coexist without collisions.

const _submit = (el, s) => {
  const body = ((s._composerDraft || '') + '').trim()
  if (!body) return
  // Use _onSend (underscore-prefixed) so DOMQL does not mistake it for a
  // DOM event handler and try to register it as an event listener for
  // the synthetic 'send' event — which never fires. The underscore prefix
  // bypasses isEventHandler() and keeps the function as a plain property
  // on the ComposerBox element, readable via el.parent._onSend.
  const onSend = el.parent?._onSend
  if (typeof onSend === 'function') onSend(body, el, s)
  s.update({ _composerDraft: '' })
}

export const ComposerBox = {
  flow: 'y',
  position: 'relative',
  padding: 'A A2 C1',
  round: 'A',
  overflow: 'hidden',
  theme: 'field',
  transition: 'B all defaultBezier',
  width: '100%',
  maxWidth: 'I1',
  margin: '0 auto',
  // Isolated state per ComposerBox instance — _composerDraft is local to
  // this component so multiple composers (AI assistant, chat, daily updates)
  // don't share the same draft slot on the root state.
  state: { _composerDraft: '' },

  SendBtn: {
    extends: 'CircleButton',
    position: 'absolute',
    bottom: 'Z',
    right: 'Z',
    zIndex: '1',
    padding: 'Z',
    icon: 'chevronUp',
    Icon: { transform: 'rotate(45deg)' },
    // Reusable brand CTA theme — canonical "submit" affordance used by
    // ContinueButton, SignupForm, LoginForm, etc. across the editor.
    // brand/designSystem/theme.js → primary:
    //   @dark : white text on electricBlue background
    //   @light: white text on gray1 (near-black) background
    // Both modes guarantee high contrast — no white-on-white possible.
    // Replaces the inline @dark override that hardcoded `background:
    // white` + `color: title` (semantic 'title' resolves to white in
    // dark mode → white text on white bg → invisible — the bug).
    theme: 'primary',
    onClick: (e, el, s) => {
      e.preventDefault()
      _submit(el, s)
    }
  },

  Editor: {
    tag: 'textarea',
    rows: '1',
    spellcheck: 'true',
    width: '100%',
    minHeight: 'C',
    maxHeight: 'F',
    padding: '0',
    outline: 'none',
    borderWidth: '0',
    background: 'transparent',
    fontSize: 'Z2',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    resize: 'none',
    overflow: 'auto',
    wordBreak: 'break-word',
    color: 'title',
    placeholder: (el, s) => {
      const ph = el.parent?.placeholder
      return typeof ph === 'function' ? ph(el, s) : (ph || 'Type something…')
    },
    value: (el, s) => s._composerDraft || '',
    onInput: (e, el, s) => {
      s.update({ _composerDraft: e.target.value })
    },
    onKeydown: (e, el, s) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        _submit(el, s)
      }
    }
  }
}
